/**
 * Supported application languages.
 */
export type SupportedLanguage = 'en' | 'de' | 'fr' | 'it';

/**
 * Fully-resolved formatting context that drives all display formatting.
 * This is the canonical contract shared between the frontend and backend.
 */
export interface FormattingContext {
  /** BCP-47 language tag for display formatting (e.g. 'en-CH', 'de-CH') */
  locale: string;
  /** ISO 4217 currency code (e.g. 'CHF', 'EUR', 'USD') */
  currency: string;
  /** IANA time zone identifier (e.g. 'Europe/Zurich', 'UTC') */
  timeZone: string;
}

/**
 * Optional per-call overrides that take precedence over the resolved context.
 */
export interface FormattingOverrides {
  locale?: string;
  currency?: string;
  timeZone?: string;
}

/**
 * Partial culture profile typically sourced from tenant configuration.
 */
export interface TenantCultureDefaults {
  locale?: string;
  currency?: string;
  timeZone?: string;
}

/**
 * System-level fallback values used when neither user nor tenant provides context.
 */
export const SYSTEM_FALLBACK: FormattingContext = {
  locale: 'en-CH',
  currency: 'CHF',
  timeZone: 'UTC',
};

/**
 * Canonical locale map from supported language codes to BCP-47 locale strings.
 * This is the single source of truth for language-to-locale resolution.
 */
export const LANGUAGE_TO_LOCALE: Record<SupportedLanguage, string> = {
  en: 'en-CH',
  de: 'de-CH',
  fr: 'fr-CH',
  it: 'it-CH',
};
